import { useEffect, useMemo, useRef, useState } from "react";
import { SoundOutlined } from "@ant-design/icons";
import { Alert, Button, Col, Form, Input, InputNumber, message, Modal, Row, Select, Switch } from "antd";
import { aiVideoApi } from "../../services/aiVideoApi";

export const AI_VIDEO_VOICE_PRESETS = Object.freeze([
  { value: "AUTO", label: "Tự động theo ngôn ngữ · đổi giọng mỗi video" },
  { value: "en-US-AriaNeural-Female", label: "Aria · English (US)", language: "en" },
  { value: "en-US-AvaNeural-Female", label: "Ava · English (US)", language: "en" },
  { value: "en-US-EmmaNeural-Female", label: "Emma · English (US)", language: "en" },
  { value: "en-US-JennyNeural-Female", label: "Jenny · English (US)", language: "en" },
  { value: "en-US-BrianNeural-Male", label: "Brian · English (US)", language: "en" },
  { value: "en-US-ChristopherNeural-Male", label: "Christopher · English (US)", language: "en" },
  { value: "en-US-GuyNeural-Male", label: "Guy · English (US)", language: "en" },
  { value: "en-US-RogerNeural-Male", label: "Roger · English (US)", language: "en" },
  { value: "en-GB-LibbyNeural-Female", label: "Libby · English (UK)", language: "en" },
  { value: "en-GB-SoniaNeural-Female", label: "Sonia · English (UK)", language: "en" },
  { value: "en-GB-RyanNeural-Male", label: "Ryan · English (UK)", language: "en" },
  { value: "en-GB-ThomasNeural-Male", label: "Thomas · English (UK)", language: "en" },
  { value: "en-AU-NatashaNeural-Female", label: "Natasha · English (Australia)", language: "en" },
  { value: "en-AU-WilliamNeural-Male", label: "William · English (Australia)", language: "en" },
  { value: "vi-VN-HoaiMyNeural-Female", label: "Hoài My · Tiếng Việt", language: "vi" },
  { value: "vi-VN-NamMinhNeural-Male", label: "Nam Minh · Tiếng Việt", language: "vi" },
  { value: "ko-KR-HyunsuMultilingualNeural-Male", label: "Hyunsu · 한국어", language: "ko" },
  { value: "ko-KR-InJoonNeural-Male", label: "InJoon · 한국어", language: "ko" },
  { value: "ja-JP-NanamiNeural-Female", label: "Nanami · 日本語 (Edge)", language: "ja" },
  { value: "ja-JP-KeitaNeural-Male", label: "Keita · 日本語 (Edge)", language: "ja" },
]);
export const AI_VIDEO_LANGUAGES = Object.freeze([
  { value: "en", label: "Tiếng Anh" },
  { value: "vi", label: "Tiếng Việt" },
  { value: "ko", label: "한국어 (Tiếng Hàn)" },
  { value: "ja", label: "日本語 (Tiếng Nhật)" },
]);
// VOICEVOX styles come from the self-hosted TTS gateway, so they are not in the static list.
export const isVoicevoxVoice = (value) => typeof value === "string" && /^voicevox:\d{1,5}$/.test(value);
const voiceAllowedFor = (language, allowed, voiceId) => allowed.has(voiceId) || (language === "ja" && isVoicevoxVoice(voiceId));
export const aiVideoVoicePresets = (language) => AI_VIDEO_VOICE_PRESETS.filter(
  (option) => option.value === "AUTO" || option.language === language,
);
export const aiVideoVoiceChoices = (language) => AI_VIDEO_VOICE_PRESETS.filter(
  (option) => option.value !== "AUTO" && option.language === language,
);
export const AI_VIDEO_SUBTITLE_PRESETS = Object.freeze([
  { value: "SOCIAL_BOLD", label: "Chữ đậm mạng xã hội" },
  { value: "CLEAN_BOTTOM", label: "Gọn gàng phía dưới" },
  { value: "WORD_HIGHLIGHT", label: "Tô sáng từng từ" },
]);
export const AI_VIDEO_BGM_PRESETS = Object.freeze([
  { value: "AUTO", label: "Tự động" },
  { value: "UPBEAT_COMMERCIAL", label: "Sôi động thương mại" },
  { value: "CORPORATE_LIGHT", label: "Doanh nghiệp nhẹ nhàng" },
  { value: "DOCUMENTARY_AMBIENT", label: "Không khí tài liệu" },
  { value: "HUMOROUS_STORY", label: "Kể chuyện hài hước" },
]);

const DEFAULT_VOICE_CONFIG = Object.freeze({
  engine: "EDGE_TTS",
  voiceId: "AUTO",
  voiceIds: Object.freeze(aiVideoVoiceChoices("en").map((option) => option.value)),
  speed: 1,
});
const DEFAULT_SUBTITLE_CONFIG = Object.freeze({ preset: "SOCIAL_BOLD" });
const DEFAULT_BGM_CONFIG = Object.freeze({ enabled: true, preset: "AUTO", volume: 0.2 });
const DEFAULT_SCHEDULE = Object.freeze({ scheduleType: "FIXED", scheduleHours: [12], scheduleMinutes: [0], intervalHourFrom: 4, intervalHourTo: 6 });

export const AI_VIDEO_PROJECT_DEFAULTS = Object.freeze({
  projectType: "MICRO_STORY",
  scriptSource: "AI_GENERATED",
  name: "",
  topic: "",
  creativeDirection: "",
  language: "en",
  targetPlatform: "TIKTOK",
  aspectRatio: "9:16",
  targetDurationSeconds: 120,
  contentPreset: "COMMERCIAL",
  aiProviderId: undefined,
  voiceConfig: DEFAULT_VOICE_CONFIG,
  subtitleConfig: DEFAULT_SUBTITLE_CONFIG,
  bgmConfig: DEFAULT_BGM_CONFIG,
  uploadGroupId: null,
  selectedChannels: {},
  schedule: DEFAULT_SCHEDULE,
  googleSheetsConfig: { connectionId: undefined, spreadsheetId: "", sheetRange: "Jobs!A:ZZ", driveRootFolderId: "" },
});

// "Video minh họa AI": AI-drawn illustration per scene instead of stock footage.
export const ILLUSTRATED_PRESET = "ILLUSTRATED_STORY";
export const isIllustratedProject = (project) => project?.contentPreset === ILLUSTRATED_PRESET;
export const AI_VIDEO_ILLUSTRATED_DEFAULTS = Object.freeze({
  ...AI_VIDEO_PROJECT_DEFAULTS,
  language: "ja",
  targetPlatform: "YOUTUBE",
  aspectRatio: "16:9",
  targetDurationSeconds: 600,
  contentPreset: ILLUSTRATED_PRESET,
  voiceConfig: { engine: "EDGE_TTS", voiceId: "AUTO", voiceIds: ["ja-JP-NanamiNeural-Female", "ja-JP-KeitaNeural-Male"], speed: 0.9 },
  bgmConfig: { enabled: true, preset: "DOCUMENTARY_AMBIENT", volume: 0.1 },
});

const objectValue = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : {};
const cleanText = (value) => typeof value === "string" && value.trim() ? value.trim() : null;
const valueAllowed = (options, value) => options.some((option) => option.value === value);
const boundedNumber = (value, minimum, maximum) => typeof value === "number" && Number.isFinite(value) && value >= minimum && value <= maximum;

function safeVoiceConfig(value, language) {
  const source = objectValue(value);
  const options = aiVideoVoiceChoices(language || "en");
  const allowed = new Set(options.map((option) => option.value));
  const configured = Array.isArray(source.voiceIds)
    ? [...new Set(source.voiceIds.filter((voiceId) => voiceAllowedFor(language, allowed, voiceId)))]
    : [];
  const voiceIds = configured.length
    ? configured
    : (allowed.has(source.voiceId) ? [source.voiceId] : options.map((option) => option.value));
  return {
    engine: "EDGE_TTS",
    voiceId: voiceIds.length === 1 ? voiceIds[0] : "AUTO",
    voiceIds,
    speed: boundedNumber(source.speed, 0.8, 1.5) ? source.speed : DEFAULT_VOICE_CONFIG.speed,
  };
}

function safeSubtitleConfig(value) {
  const source = objectValue(value);
  return { preset: valueAllowed(AI_VIDEO_SUBTITLE_PRESETS, source.preset) ? source.preset : DEFAULT_SUBTITLE_CONFIG.preset };
}

function safeBgmConfig(value) {
  const source = objectValue(value);
  return {
    enabled: typeof source.enabled === "boolean" ? source.enabled : DEFAULT_BGM_CONFIG.enabled,
    preset: valueAllowed(AI_VIDEO_BGM_PRESETS, source.preset) ? source.preset : DEFAULT_BGM_CONFIG.preset,
    volume: boundedNumber(source.volume, 0, 0.4) ? source.volume : DEFAULT_BGM_CONFIG.volume,
  };
}

export function aiVideoMediaConfigs(values) {
  const voice = objectValue(values?.voiceConfig);
  const subtitle = objectValue(values?.subtitleConfig);
  const bgm = objectValue(values?.bgmConfig);
  const voiceEngine = voice.engine == null ? "EDGE_TTS" : voice.engine;
  const voiceOptions = values?.language
    ? aiVideoVoiceChoices(values.language)
    : AI_VIDEO_VOICE_PRESETS.filter((option) => option.value !== "AUTO");
  const allowedVoiceIds = new Set(voiceOptions.map((option) => option.value));
  const hasVoiceIds = Array.isArray(voice.voiceIds);
  const configuredVoiceIds = hasVoiceIds ? [...new Set(voice.voiceIds)] : [];
  const voiceIds = hasVoiceIds
    ? configuredVoiceIds
    : (allowedVoiceIds.has(voice.voiceId) ? [voice.voiceId]
      : (voice.voiceId === "AUTO" ? voiceOptions.map((option) => option.value) : []));
  if (voiceEngine !== "EDGE_TTS" || !voiceIds.length
      || voiceIds.some((voiceId) => !voiceAllowedFor(values?.language, allowedVoiceIds, voiceId))
      || !boundedNumber(voice.speed, 0.8, 1.5)
      || !valueAllowed(AI_VIDEO_SUBTITLE_PRESETS, subtitle.preset)
      || typeof bgm.enabled !== "boolean" || !valueAllowed(AI_VIDEO_BGM_PRESETS, bgm.preset)
      || !boundedNumber(bgm.volume, 0, 0.4)) {
    throw new Error("AI_VIDEO_CONFIG_VALIDATION");
  }
  return {
    voiceConfig: {
      engine: "EDGE_TTS",
      voiceId: voiceIds.length === 1 ? voiceIds[0] : "AUTO",
      voiceIds,
      speed: voice.speed,
    },
    subtitleConfig: { preset: subtitle.preset },
    bgmConfig: { enabled: bgm.enabled, preset: bgm.preset, volume: bgm.volume },
  };
}

export function eligibleByokProviders(providers = [], profile = {}) {
  return providers.filter((provider) =>
    provider?.enabled === true
    && provider?.credentialScope === "USER"
    && String(provider?.ownerId || "") === String(profile?.id || "")
    && Array.isArray(provider?.capabilities)
    && provider.capabilities.some((capability) => String(capability).trim().toLowerCase() === "script")
  );
}

export const shouldShowAiVideoProviderSelector = (profile) => profile?.aiVideoPlanMode === "BYOK";

const itemLabel = (item, fields) => fields.map((field) => item?.[field]).find(Boolean) || item?.id;
const lookupItems = (ids, items) => (Array.isArray(ids) ? ids : []).map((id) => {
  const item = (Array.isArray(items) ? items : []).find((candidate) => String(candidate?.id) === String(id));
  return item || { id };
});

export function uploadGroupDestinations(group, youtubeChannels = [], tiktokAccounts = [], facebookPages = []) {
  if (!group) return null;
  return {
    youtube: lookupItems(group.youtubeChannelIds, youtubeChannels).map((item) => itemLabel(item, ["channelTitle", "channelName", "channelId"])),
    tiktok: lookupItems(group.tiktokAccountIds, tiktokAccounts).map((item) => itemLabel(item, ["displayName", "username", "accountId"])),
    facebook: lookupItems(group.facebookPageIds, facebookPages).map((item) => itemLabel(item, ["pageName", "pageId", "pageUrl"])),
  };
}

const destinationOptions = (items, fields) => {
  return (Array.isArray(items) ? items : [])
    .map((item) => ({ value: item.id, label: itemLabel(item, fields) }));
};

function safeSchedule(value) {
  const source = objectValue(value);
  return {
    scheduleType: source.scheduleType === "RANDOM" ? "RANDOM" : "FIXED",
    scheduleHours: Array.isArray(source.scheduleHours) ? source.scheduleHours : DEFAULT_SCHEDULE.scheduleHours,
    scheduleMinutes: Array.isArray(source.scheduleMinutes) ? source.scheduleMinutes : DEFAULT_SCHEDULE.scheduleMinutes,
    intervalHourFrom: boundedNumber(source.intervalHourFrom, 1, 168) ? source.intervalHourFrom : DEFAULT_SCHEDULE.intervalHourFrom,
    intervalHourTo: boundedNumber(source.intervalHourTo, 1, 168) ? source.intervalHourTo : DEFAULT_SCHEDULE.intervalHourTo,
  };
}

export function projectFormValues(project, profile, providers = []) {
  const source = project || AI_VIDEO_PROJECT_DEFAULTS;
  const providerIds = new Set(eligibleByokProviders(providers, profile).map(({ id }) => String(id)));
  return {
    projectType: source.projectType || (source.contentPreset === "FOOTBALL_QUEUE" ? "GOOGLE_SHEETS" : "MICRO_STORY"),
    scriptSource: source.scriptSource === "GOOGLE_SHEETS" ? "GOOGLE_SHEETS" : "AI_GENERATED",
    name: source.name ?? "",
    topic: source.topic ?? "",
    creativeDirection: source.creativeDirection ?? "",
    language: source.language || "en",
    targetPlatform: source.targetPlatform || "TIKTOK",
    aspectRatio: source.aspectRatio || "9:16",
    targetDurationSeconds: source.targetDurationSeconds ?? 120,
    contentPreset: source.contentPreset || "COMMERCIAL",
    aiProviderId: profile?.aiVideoPlanMode === "BYOK" && providerIds.has(String(source.aiProviderId))
      ? source.aiProviderId
      : undefined,
    voiceConfig: safeVoiceConfig(source.voiceConfig, source.language || "en"),
    subtitleConfig: safeSubtitleConfig(source.subtitleConfig),
    bgmConfig: safeBgmConfig(source.bgmConfig),
    uploadGroupId: source.uploadGroupId || null,
    selectedChannels: objectValue(source.selectedChannels),
    schedule: safeSchedule(source.schedule),
    googleSheetsConfig: {
      connectionId: source.googleSheetsConfig?.connectionId,
      spreadsheetId: source.googleSheetsConfig?.spreadsheetId || "",
      sheetRange: source.googleSheetsConfig?.sheetRange || "Jobs!A:ZZ",
      driveRootFolderId: source.googleSheetsConfig?.driveRootFolderId || "",
    },
  };
}

export function aiVideoProjectPayload(values, profile, providers = []) {
  if (profile?.aiVideoPlanMode === "DISABLED") throw new Error("AI_VIDEO_DISABLED");
  const name = cleanText(values?.name);
  const targetDurationSeconds = Number(values?.targetDurationSeconds);
  if (!Number.isInteger(targetDurationSeconds) || targetDurationSeconds < 1) {
    throw new Error("AI_VIDEO_DURATION_VALIDATION");
  }
  const projectType = values?.projectType === "GOOGLE_SHEETS" ? "GOOGLE_SHEETS" : "MICRO_STORY";
  if (projectType === "GOOGLE_SHEETS") {
    const config = objectValue(values?.googleSheetsConfig);
    const connectionId = cleanText(config.connectionId);
    const spreadsheetId = cleanText(config.spreadsheetId);
    if (!name || name.length > 200 || !connectionId || !spreadsheetId) throw new Error("AI_VIDEO_GOOGLE_SHEETS_VALIDATION");
    return {
      projectType,
      scriptSource: "AI_GENERATED",
      name,
      topic: "Google Sheets render queue",
      creativeDirection: "Render the pre-authored assets and script from the selected Google Sheets row.",
      language: "en",
      targetPlatform: "YOUTUBE",
      aspectRatio: "16:9",
      targetDurationSeconds,
      contentPreset: "FOOTBALL_QUEUE",
      aiProviderId: profile?.aiVideoPlanMode === "BYOK" ? values.aiProviderId : null,
      voiceConfig: { ...DEFAULT_VOICE_CONFIG, voiceIds: [...DEFAULT_VOICE_CONFIG.voiceIds] },
      subtitleConfig: { preset: "SOCIAL_BOLD" },
      bgmConfig: { enabled: true, preset: "AUTO", volume: 0.2 },
      uploadGroupId: values.uploadGroupId || null,
      selectedChannels: objectValue(values.selectedChannels),
      schedule: safeSchedule(values.schedule),
      googleSheetsConfig: {
        connectionId,
        spreadsheetId,
        sheetRange: cleanText(config.sheetRange) || "Jobs!A:ZZ",
        driveRootFolderId: cleanText(config.driveRootFolderId) || "",
      },
    };
  }
  const topic = cleanText(values?.topic);
  const contentPreset = cleanText(values?.contentPreset) || "COMMERCIAL";
  // Micro Story: 9:16 vertical shorts or 16:9 landscape long-form (YouTube).
  const expectedAspectRatio = contentPreset === "FOOTBALL_QUEUE" ? "16:9"
    : (values?.aspectRatio === "16:9" ? "16:9" : "9:16");
  const scriptSource = values?.scriptSource === "GOOGLE_SHEETS" ? "GOOGLE_SHEETS" : "AI_GENERATED";
  const creativeDirection = cleanText(values?.creativeDirection);
  if (!name || name.length > 200 || (topic?.length || 0) > 10000 || (creativeDirection?.length || 0) > 10000
      || (scriptSource === "AI_GENERATED" && (!topic || !creativeDirection))
      || values?.aspectRatio !== expectedAspectRatio) {
    throw new Error("AI_VIDEO_VALIDATION");
  }
  const mediaConfigs = aiVideoMediaConfigs(values);
  let googleSheetsConfig;
  if (scriptSource === "GOOGLE_SHEETS") {
    const config = objectValue(values?.googleSheetsConfig);
    const connectionId = cleanText(config.connectionId);
    const spreadsheetId = cleanText(config.spreadsheetId);
    if (!connectionId || !spreadsheetId) throw new Error("AI_VIDEO_GOOGLE_SHEETS_VALIDATION");
    googleSheetsConfig = {
      connectionId,
      spreadsheetId,
      sheetRange: cleanText(config.sheetRange) || "Jobs!A:ZZ",
      driveRootFolderId: cleanText(config.driveRootFolderId) || "",
    };
  }

  let aiProviderId = null;
  if (profile?.aiVideoPlanMode === "BYOK") {
    const eligibleIds = new Set(eligibleByokProviders(providers, profile).map(({ id }) => String(id)));
    if (!values.aiProviderId || !eligibleIds.has(String(values.aiProviderId))) {
      throw new Error("AI_VIDEO_PROVIDER_REQUIRED");
    }
    aiProviderId = values.aiProviderId;
  }

  return {
    projectType,
    scriptSource,
    name,
    topic,
    creativeDirection,
    language: cleanText(values.language) || "en",
    targetPlatform: cleanText(values.targetPlatform) || "TIKTOK",
    aspectRatio: expectedAspectRatio,
    targetDurationSeconds,
    contentPreset,
    aiProviderId,
    ...mediaConfigs,
    uploadGroupId: values.uploadGroupId || null,
    selectedChannels: objectValue(values.selectedChannels),
    schedule: safeSchedule(values.schedule),
    ...(googleSheetsConfig ? { googleSheetsConfig } : {}),
  };
}

function VoicePreviewControl({ disabled, extraOptions = [] }) {
  const options = [...AI_VIDEO_VOICE_PRESETS.filter((option) => option.value !== "AUTO"), ...extraOptions];
  const [open, setOpen] = useState(false);
  const [voiceId, setVoiceId] = useState("vi-VN-HoaiMyNeural-Female");
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);
  const objectUrlRef = useRef(null);

  useEffect(() => () => {
    audioRef.current?.pause();
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  const preview = async () => {
    setLoading(true);
    try {
      audioRef.current?.pause();
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const { blob } = await aiVideoApi.voicePreview(voiceId);
      const objectUrl = URL.createObjectURL(blob);
      objectUrlRef.current = objectUrl;
      const audio = new Audio(objectUrl);
      audioRef.current = audio;
      await audio.play();
    } catch (error) {
      message.error(error?.message || "Không thể nghe thử giọng đọc.");
    } finally {
      setLoading(false);
    }
  };

  return <>
    <Button disabled={disabled} icon={<SoundOutlined />} onClick={() => setOpen(true)}>Nghe thử giọng đọc</Button>
    <Modal title="Nghe thử giọng đọc (Edge TTS và VOICEVOX)" open={open} destroyOnClose footer={null}
      onCancel={() => { audioRef.current?.pause(); setOpen(false); }}>
      <Select showSearch style={{ width: "100%", marginBottom: 16 }} value={voiceId} onChange={setVoiceId}
        optionFilterProp="label" options={options} />
      <Button type="primary" icon={<SoundOutlined />} loading={loading} onClick={preview}>Phát câu đọc thử</Button>
    </Modal>
  </>;
}

export default function AiVideoProjectForm({ project, profile, providers = [], workspaceConnections = [], onConnectWorkspace, onSubmit, submitting = false, readOnly = false, form: providedForm, hideSubmit = false, variant = "STANDARD" }) {
  const [internalForm] = Form.useForm();
  const form = providedForm || internalForm;
  const illustrated = variant === "ILLUSTRATED" || isIllustratedProject(project);
  const eligibleProviders = useMemo(() => eligibleByokProviders(providers, profile), [providers, profile]);
  const values = useMemo(() => projectFormValues(project || (illustrated ? AI_VIDEO_ILLUSTRATED_DEFAULTS : null), profile, providers), [project, illustrated, profile, providers]);
  const disabled = readOnly || profile?.aiVideoPlanMode === "DISABLED";
  const [projectType, setProjectType] = useState(values.projectType);
  const [scriptSource, setScriptSource] = useState(values.scriptSource);
  const [selectedLanguage, setSelectedLanguage] = useState(values.language);
  const [voicevoxOptions, setVoicevoxOptions] = useState([]);
  const [voicevoxWarning, setVoicevoxWarning] = useState(null);
  const voicevoxRequested = useRef(false);
  useEffect(() => {
    if (selectedLanguage !== "ja" || voicevoxRequested.current) return;
    voicevoxRequested.current = true;
    aiVideoApi.voicevoxVoices().then((result) => {
      const voices = Array.isArray(result?.voices) ? result.voices : [];
      setVoicevoxOptions(voices.filter((voice) => isVoicevoxVoice(voice?.value))
        .map((voice) => ({ value: voice.value, label: voice.label || voice.value, language: "ja" })));
      const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
      setVoicevoxWarning(voices.length ? null : (warnings[0] || "Chưa có giọng VOICEVOX từ TTS gateway."));
    }).catch(() => {
      voicevoxRequested.current = false;
      setVoicevoxWarning("Không tải được danh sách giọng VOICEVOX.");
    });
  }, [selectedLanguage]);
  const voiceOptions = useMemo(() => {
    const base = aiVideoVoiceChoices(selectedLanguage);
    if (selectedLanguage !== "ja") return base;
    const known = new Set(voicevoxOptions.map((option) => option.value));
    const saved = (values.voiceConfig?.voiceIds || []).filter((voiceId) => isVoicevoxVoice(voiceId) && !known.has(voiceId))
      .map((voiceId) => ({ value: voiceId, label: `VOICEVOX · style ${voiceId.split(":")[1]}`, language: "ja" }));
    return [...base, ...voicevoxOptions, ...saved];
  }, [selectedLanguage, voicevoxOptions, values.voiceConfig]);
  const lastSyncedValues = useRef(null);

  useEffect(() => {
    const serialized = JSON.stringify(values);
    if (lastSyncedValues.current === serialized) return;
    lastSyncedValues.current = serialized;
    form.setFieldsValue(values);
    setProjectType(values.projectType);
    setScriptSource(values.scriptSource);
    setSelectedLanguage(values.language);
  }, [form, values]);

  const submit = (nextValues) => onSubmit(aiVideoProjectPayload({ ...values, ...nextValues }, profile, providers));

  return <Form form={form} layout="vertical" initialValues={values} onFinish={submit} onValuesChange={(changed) => {
    if (changed.projectType) setProjectType(changed.projectType);
    if (changed.scriptSource) setScriptSource(changed.scriptSource);
    if (changed.language) {
      setSelectedLanguage(changed.language);
      const voiceIds = aiVideoVoiceChoices(changed.language).map((option) => option.value);
      form.setFieldsValue({ voiceConfig: { ...form.getFieldValue("voiceConfig"), voiceId: "AUTO", voiceIds } });
    }
  }}>
    {profile?.aiVideoPlanMode === "DISABLED" && <Alert showIcon type="warning" message="Gói AI Video đang bị tắt" description="Bạn không thể tạo hoặc chỉnh sửa dự án." />}
    {illustrated && <Alert showIcon type="info" style={{ marginBottom: 16 }} message="Video minh họa AI"
      description="Mỗi cảnh được vẽ bằng AI theo đúng nội dung lời đọc (không dùng video stock). Phong cách tranh và nhân vật mô tả trong System prompt, ví dụ: tranh màu nước nhẹ nhàng, nền trắng, ông bà người Nhật hiền hậu." />}
    <div className={`ai-video-project-form-grid ${projectType === "MICRO_STORY" ? "ai-video-project-form-grid--micro" : ""}`}>
    <Form.Item hidden={illustrated} className="ai-video-field-project-type" name="projectType" label="Loại dự án" rules={[{ required: true, message: "Chọn loại dự án" }]}>
      <Select disabled={disabled || Boolean(project)} options={[{ value: "MICRO_STORY", label: "Micro Story" }, { value: "GOOGLE_SHEETS", label: "Render theo Google Sheets" }]} />
    </Form.Item>
    <Form.Item className="ai-video-field-name" name="name" label="Tên dự án" rules={[{ required: true, whitespace: true, message: "Nhập tên dự án" }, { max: 200, message: "Tên tối đa 200 ký tự" }]}>
      <Input maxLength={200} disabled={disabled} />
    </Form.Item>
    {projectType === "MICRO_STORY" && <>
    <Form.Item hidden={illustrated} className="ai-video-field-script-source" name="scriptSource" label="Nguồn kịch bản" rules={[{ required: true, message: "Chọn nguồn kịch bản" }]}>
      <Select disabled={disabled || Boolean(project)} options={[
        { value: "AI_GENERATED", label: "AI tự tạo" },
        { value: "GOOGLE_SHEETS", label: "Google Sheets" },
      ]} />
    </Form.Item>
    {scriptSource === "AI_GENERATED" && <>
    <Form.Item className="ai-video-field-topic" name="topic" label="Chủ đề" rules={[{ required: true, whitespace: true, message: "Nhập chủ đề video" }, { max: 10000, message: "Chủ đề tối đa 10.000 ký tự" }]}>
      <Input.TextArea rows={8} maxLength={10000} disabled={disabled} />
    </Form.Item>
    <Form.Item className="ai-video-field-creative-direction" name="creativeDirection" label="System prompt" rules={[{ required: true, whitespace: true, message: "Nhập system prompt" }, { max: 10000, message: "Nội dung tối đa 10.000 ký tự" }]}>
      <Input.TextArea rows={8} maxLength={10000} disabled={disabled} />
    </Form.Item>
    </>}
    <Form.Item className="ai-video-field-language" name="language" label="Ngôn ngữ" rules={[{ required: true, message: "Chọn ngôn ngữ" }]}>
      <Select disabled={disabled} options={AI_VIDEO_LANGUAGES} />
    </Form.Item>
    <div className="ai-video-field-frame-duration">
      <Form.Item name="aspectRatio" label="Tỷ lệ khung hình" rules={[{ required: true }]}>
        <Select disabled={disabled} options={[{ value: "9:16", label: "9:16 (dọc)" }, { value: "16:9", label: "16:9 (ngang)" }]} />
      </Form.Item>
      <Form.Item name="targetDurationSeconds" label="Thời lượng tối thiểu (giây)" rules={[
        { required: true, message: "Nhập thời lượng tối thiểu" },
        { type: "number", min: 1, message: "Thời lượng tối thiểu phải lớn hơn 0 giây" },
      ]}>
        <InputNumber disabled={disabled} min={1} precision={0} style={{ width: "100%" }} />
      </Form.Item>
    </div>
    <Form.Item hidden={illustrated} name="contentPreset" label="Chất lượng nội dung">
      <Select disabled={disabled} options={[{ value: "COMMERCIAL", label: "Thương mại chất lượng cao" }, { value: "DOCUMENTARY", label: "Tài liệu / chiêm nghiệm" }]} />
    </Form.Item>
    <Form.Item name={["voiceConfig", "voiceIds"]} label={selectedLanguage === "ja" ? "Giọng đọc (Edge TTS / VOICEVOX)" : "Giọng đọc Edge TTS"}
      rules={[{ required: true, type: "array", min: 1, message: "Chọn ít nhất một giọng đọc" }]}
      extra={selectedLanguage === "ja" && voicevoxWarning ? voicevoxWarning : null}>
      <Select mode="multiple" allowClear showSearch optionFilterProp="label" disabled={disabled} options={voiceOptions} placeholder="Chọn một hoặc nhiều giọng đọc" />
    </Form.Item>
    <VoicePreviewControl disabled={disabled} extraOptions={voicevoxOptions} />
    <Form.Item name={["voiceConfig", "speed"]} label="Tốc độ giọng đọc" rules={[{ required: true }, { type: "number", min: 0.8, max: 1.5, message: "Tốc độ phải từ 0,8 đến 1,5" }]}>
      <InputNumber disabled={disabled} min={0.8} max={1.5} step={0.1} precision={1} style={{ width: "100%" }} />
    </Form.Item>
    <Form.Item name={["subtitleConfig", "preset"]} label="Kiểu phụ đề" rules={[{ required: true, message: "Chọn kiểu phụ đề" }]}>
      <Select disabled={disabled} options={AI_VIDEO_SUBTITLE_PRESETS} />
    </Form.Item>
    <div className="ai-video-field-bgm-style">
      <Form.Item name={["bgmConfig", "enabled"]} label="Nhạc nền" valuePropName="checked">
        <Switch disabled={disabled} checkedChildren="Bật" unCheckedChildren="Tắt" />
      </Form.Item>
      <Form.Item name={["bgmConfig", "preset"]} label="Phong cách nhạc nền" rules={[{ required: true, message: "Chọn phong cách nhạc nền" }]}>
        <Select disabled={disabled} options={AI_VIDEO_BGM_PRESETS} />
      </Form.Item>
    </div>
    <Form.Item name={["bgmConfig", "volume"]} label="Âm lượng nhạc nền" rules={[{ required: true }, { type: "number", min: 0, max: 0.4, message: "Âm lượng phải từ 0 đến 0,4" }]}>
      <InputNumber disabled={disabled} min={0} max={0.4} step={0.05} precision={2} style={{ width: "100%" }} />
    </Form.Item>
    {shouldShowAiVideoProviderSelector(profile) && <Form.Item name="aiProviderId" label="Nhà cung cấp AI" rules={[{ required: true, message: "Chọn nhà cung cấp AI đang hoạt động" }]} extra={eligibleProviders.length ? null : "Cần một provider USER do bạn sở hữu, đang bật và có capability script."}>
      <Select disabled={disabled} placeholder="Chọn provider" options={eligibleProviders.map((provider) => ({ value: provider.id, label: provider.name }))} />
    </Form.Item>}
    </>}
    {(projectType === "GOOGLE_SHEETS" || (projectType === "MICRO_STORY" && scriptSource === "GOOGLE_SHEETS")) && <>
      <Form.Item name={["googleSheetsConfig", "connectionId"]} label="Kết nối Google Workspace" rules={[{ required: true, message: "Chọn Google Workspace" }]} extra={onConnectWorkspace ? <Button type="link" style={{ padding: 0 }} onClick={onConnectWorkspace}>Kết nối workspace mới</Button> : null}>
        <Select disabled={disabled} placeholder="Chọn workspace đã kết nối" options={(workspaceConnections || []).map((item) => ({ value: item.id, label: item.email || "Google Workspace" }))} />
      </Form.Item>
      <Form.Item name={["googleSheetsConfig", "spreadsheetId"]} label="Google Sheet URL hoặc Spreadsheet ID" rules={[{ required: true, whitespace: true, message: "Nhập Google Sheet URL hoặc Spreadsheet ID" }]}>
        <Input disabled={disabled} placeholder="https://docs.google.com/spreadsheets/d/..." />
      </Form.Item>
      <Form.Item name={["googleSheetsConfig", "sheetRange"]} label="Vùng dữ liệu (A1 notation)" rules={[{ required: true, whitespace: true, message: "Nhập vùng dữ liệu" }]}>
        <Input disabled={disabled} placeholder="Jobs!A:ZZ" />
      </Form.Item>
      <Form.Item name={["googleSheetsConfig", "driveRootFolderId"]} label="Google Drive root folder URL hoặc ID">
        <Input disabled={disabled} placeholder="https://drive.google.com/drive/folders/..." />
      </Form.Item>
    </>}
    </div>
    {!readOnly && !hideSubmit && <Button type="primary" htmlType="submit" loading={submitting} disabled={disabled}>{project ? "Lưu thay đổi" : "Tạo dự án"}</Button>}
  </Form>;
}

export function AiVideoUploadSettings({ project, profile, providers = [], youtubeChannels = [], tiktokAccounts = [], facebookPages = [], onSubmit, submitting = false, readOnly = false, form: providedForm, hideSubmit = false }) {
  const [internalForm] = Form.useForm();
  const form = providedForm || internalForm;
  const projectValues = useMemo(() => projectFormValues(project, profile, providers), [project, profile, providers]);
  const values = useMemo(() => ({
    selectedChannels: projectValues.selectedChannels,
    schedule: projectValues.schedule,
  }), [projectValues]);
  const disabled = readOnly || profile?.aiVideoPlanMode === "DISABLED";
  const lastSyncedValues = useRef(null);

  useEffect(() => {
    const serialized = JSON.stringify(values);
    if (lastSyncedValues.current === serialized) return;
    lastSyncedValues.current = serialized;
    form.setFieldsValue(values);
  }, [form, values]);

  const submit = (nextValues) => onSubmit(aiVideoProjectPayload({ ...projectValues, ...nextValues, uploadGroupId: null }, profile, providers));

  return <Form form={form} layout="vertical" initialValues={values} onFinish={submit}>
    <Row gutter={16}>
      <Col xs={24} md={8}><Form.Item name={["selectedChannels", "youtubeChannelIds"]} label="YouTube Channels">
        <Select mode="multiple" disabled={disabled} placeholder="Chọn kênh YouTube" options={destinationOptions(youtubeChannels, ["channelTitle", "channelName", "channelId"])} />
      </Form.Item></Col>
      <Col xs={24} md={8}><Form.Item name={["selectedChannels", "tiktokAccountIds"]} label="TikTok Accounts">
        <Select mode="multiple" disabled={disabled} placeholder="Chọn account TikTok" options={destinationOptions(tiktokAccounts, ["displayName", "username", "accountId"])} />
      </Form.Item></Col>
      <Col xs={24} md={8}><Form.Item name={["selectedChannels", "facebookPageIds"]} label="Facebook Pages">
        <Select mode="multiple" disabled={disabled} placeholder="Chọn page Facebook" options={destinationOptions(facebookPages, ["pageName", "pageId", "pageUrl"])} />
      </Form.Item></Col>
    </Row>
    <Row gutter={16}>
      <Col xs={24} md={8}><Form.Item name={["schedule", "scheduleType"]} label="Kiểu lịch">
        <Select disabled={disabled} options={[{ value: "FIXED", label: "Cố định" }, { value: "RANDOM", label: "Ngẫu nhiên" }]} />
      </Form.Item></Col>
      <Form.Item noStyle shouldUpdate={(previous, current) => previous.schedule?.scheduleType !== current.schedule?.scheduleType}>
        {({ getFieldValue }) => getFieldValue(["schedule", "scheduleType"]) === "RANDOM" ? <>
          <Col xs={24} md={8}><Form.Item name={["schedule", "intervalHourFrom"]} label="Cách từ (giờ)"><InputNumber disabled={disabled} min={1} style={{ width: "100%" }} /></Form.Item></Col>
          <Col xs={24} md={8}><Form.Item name={["schedule", "intervalHourTo"]} label="Đến (giờ)"><InputNumber disabled={disabled} min={1} style={{ width: "100%" }} /></Form.Item></Col>
        </> : <>
          <Col xs={24} md={8}><Form.Item name={["schedule", "scheduleHours"]} label="Giờ upload"><Select mode="multiple" disabled={disabled} placeholder="Chọn giờ" options={Array.from({ length: 24 }, (_, value) => ({ value, label: `${String(value).padStart(2, "0")}:00` }))} /></Form.Item></Col>
          <Col xs={24} md={8}><Form.Item name={["schedule", "scheduleMinutes"]} label="Phút upload"><Select mode="multiple" disabled={disabled} placeholder="Chọn phút" options={Array.from({ length: 12 }, (_, index) => ({ value: index * 5, label: String(index * 5).padStart(2, "0") }))} /></Form.Item></Col>
        </>}
      </Form.Item>
    </Row>
    {!readOnly && !hideSubmit && <Button htmlType="submit" loading={submitting} disabled={disabled}>Lưu cấu hình upload</Button>}
  </Form>;
}
